import { Injectable, inject } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { collection, doc, getDoc, getDocs, runTransaction, serverTimestamp } from 'firebase/firestore';
import { Storage, ref, getDownloadURL } from '@angular/fire/storage';
import { Auth } from '@angular/fire/auth';
import { PhotoboothService } from './photobooth.service';
import {
  FRAME_ARTWORKS_COLLECTION,
  FRAME_OWNERSHIP_MESSAGES,
  FrameCatalogEntry,
  FrameGroupSource,
  FrameOwnership,
  buildFrameCatalog,
  isAssignableArtist,
  ownershipDocId
} from '../utils/frame-ownership.util';

/** Thrown when the owner changed between reading it and saving (someone else edited the frame meanwhile). */
export class FrameOwnerChangedError extends Error {
  constructor(readonly currentArtistId: string | null) {
    super(FRAME_OWNERSHIP_MESSAGES.ownerChanged);
  }
}

/**
 * KAN-149: the one place that reads and writes `frame_artworks/{frameId}` (one artist per frame ID, null = company-owned).
 * Photobooth Management and Artist Management both go through it, so they always show the same owner.
 */
@Injectable({ providedIn: 'root' })
export class FrameOwnershipService {
  private firestore = inject(Firestore);
  private storage = inject(Storage);
  private auth = inject(Auth);
  private photoboothService = inject(PhotoboothService);
  private imageUrls = new Map<string, Promise<string | null>>();
  /** Why a frame has no thumbnail: every place frameImageUrl looked (shown on hover over "No Img"). */
  readonly imageDiagnostics = new Map<string, string>();

  async getOwnership(frameId: string): Promise<FrameOwnership | null> {
    const snap = await getDoc(doc(this.firestore, FRAME_ARTWORKS_COLLECTION, ownershipDocId(frameId)));
    return snap.exists() ? ({ frameId, ...snap.data() } as FrameOwnership) : null;
  }

  /** Every ownership record, keyed by frame ID (soft-deleted records included; callers treat them as unassigned). */
  async getAllOwnerships(): Promise<Map<string, FrameOwnership>> {
    const snap = await getDocs(collection(this.firestore, FRAME_ARTWORKS_COLLECTION));
    const map = new Map<string, FrameOwnership>();
    snap.docs.forEach(d => {
      const data = d.data() as FrameOwnership;
      if (data.frameId) map.set(data.frameId, { ...data, artistId: data.artistId ?? null });
    });
    return map;
  }

  /**
   * Sets the owner of a frame ID (`artistId` null = Unassigned (company)). `expectedArtistId` is the owner the user saw; if the
   * stored owner differs, nothing is written and FrameOwnerChangedError is thrown (pass undefined to skip the check). The target
   * artist must be active and not deleted, read inside the same transaction.
   */
  async setOwner(frameId: string, artistId: string | null, fpImagePath: string | null, expectedArtistId?: string | null): Promise<void> {
    const ownerRef = doc(this.firestore, FRAME_ARTWORKS_COLLECTION, ownershipDocId(frameId));
    const user = this.auth.currentUser;
    await runTransaction(this.firestore, async tx => {
      const snap = await tx.get(ownerRef);
      const stored = snap.exists() ? (snap.data() as FrameOwnership) : null;
      const current = stored && stored.isDeleted !== true ? stored.artistId ?? null : null;
      if (expectedArtistId !== undefined && current !== expectedArtistId) throw new FrameOwnerChangedError(current);
      if (artistId) {
        const artistSnap = await tx.get(doc(this.firestore, 'artists', artistId));
        if (!artistSnap.exists() || !isAssignableArtist({ id: artistId, ...(artistSnap.data() as any) })) {
          throw new Error(FRAME_OWNERSHIP_MESSAGES.artistNotAssignable);
        }
      }
      tx.set(ownerRef, {
        frameId,
        artistId,
        fpImagePath: fpImagePath ?? stored?.fpImagePath ?? null,
        isDeleted: false,
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || user?.uid || 'unknown'
      });
    });
  }

  /** All frames in the global and every device draft frame group, one entry per frame ID. */
  async getFrameCatalog(): Promise<FrameCatalogEntry[]> {
    const globalThemes = await this.themeIds(() => this.photoboothService.getGlobalLayout());
    const sources: FrameGroupSource[] = await this.groupSources('global', null, 'Global', globalThemes);
    const devices = await this.photoboothService.getAllDevices();
    const perDevice = await Promise.all(devices.map(async d => {
      const deviceThemes = await this.themeIds(() => this.photoboothService.getDeviceLayout(d.id));
      const name = d.data?.deviceName || d.data?.name || d.id;
      return this.groupSources('device', d.id, name, [...new Set([...deviceThemes, ...globalThemes])]);
    }));
    perDevice.forEach(list => sources.push(...list));
    return buildFrameCatalog(sources);
  }

  /**
   * Thumbnail of a catalog frame: its draft `_FP` file when that exists, otherwise the same resolution the Frame Report uses
   * (`PhotoboothService.resolveFrameImageUrl`: published frame config first, then a storage search), tried per copy.
   * Cached per frame ID.
   */
  frameImageUrl(entry: FrameCatalogEntry): Promise<string | null> {
    const key = `frame:${entry.frameId}`;
    let pending = this.imageUrls.get(key);
    if (!pending) {
      pending = (async () => {
        const trace: string[] = [];
        const direct = await this.imageUrl(entry.fpImagePath);
        if (direct) return direct;
        trace.push(entry.fpImagePath ? `Draft _FP file not found: ${entry.fpImagePath}` : 'No _FP file name in the frame entry (fpName missing).');
        const copies = [...entry.copies].sort((a, b) => (a.scope === b.scope ? 0 : a.scope === 'device' ? -1 : 1));
        for (const copy of copies) {
          const where = `${copy.scope === 'global' ? 'Global' : (copy.deviceName || copy.deviceId)} (theme "${copy.themeId}", group "${copy.groupId}")`;
          if (!copy.themeId || !copy.groupId) {
            trace.push(`${where}: skipped, theme or group could not be read from the group ID.`);
            continue;
          }
          try {
            const result = await this.photoboothService.resolveFrameImageUrl(copy.deviceId || 'global', copy.themeId, copy.groupId, entry.frameId);
            if (result.url) return result.url;
            trace.push(`${where}: ${result.debugPath}`);
          } catch (e: any) {
            trace.push(`${where}: lookup failed (${e?.message || e}).`);
          }
        }
        this.imageDiagnostics.set(entry.frameId, trace.join('\n'));
        return null;
      })();
      this.imageUrls.set(key, pending);
    }
    return pending;
  }

  /**
   * Image of a printed frame (statements, KAN-147): the registry `_FP` file when it exists, otherwise exactly the Frame Report
   * call, `resolveFrameImageUrl(deviceId, themeId, frameGroupId, frameId)`, with the device, theme and group the kiosk recorded
   * on the monthly counter. Cached per frame ID.
   */
  usageImageUrl(frameId: string, fpImagePath: string | null, source?: { deviceId: string; themeId: string; frameGroupId: string }): Promise<string | null> {
    const key = `usage:${frameId}`;
    let pending = this.imageUrls.get(key);
    if (!pending) {
      pending = (async () => {
        const direct = await this.imageUrl(fpImagePath);
        if (direct || !source) return direct;
        try {
          const result = await this.photoboothService.resolveFrameImageUrl(source.deviceId || 'ALL', source.themeId, source.frameGroupId, frameId);
          return result.url;
        } catch {
          return null;
        }
      })();
      this.imageUrls.set(key, pending);
    }
    return pending;
  }

  /** Download URL of a frame's `_FP` image, cached per path; null when there is no image or it cannot be read. */
  imageUrl(path: string | null | undefined): Promise<string | null> {
    if (!path) return Promise.resolve(null);
    let pending = this.imageUrls.get(path);
    if (!pending) {
      pending = getDownloadURL(ref(this.storage, path)).catch(() => null);
      this.imageUrls.set(path, pending);
    }
    return pending;
  }

  private async groupSources(scope: 'global' | 'device', deviceId: string | null, deviceName: string, knownThemeIds: string[]): Promise<FrameGroupSource[]> {
    const base = scope === 'global' ? 'global' : deviceId;
    try {
      const snap = await getDocs(collection(this.firestore, `photobooth_config/${base}/frame_groups`));
      return snap.docs.map(d => ({
        scope,
        deviceId,
        deviceName,
        groupDocId: d.id,
        groupName: d.data()['name'] || '',
        knownThemeIds,
        uploadedFrames: d.data()['uploadedFrames'] || []
      }));
    } catch (e) {
      console.warn(`Could not read frame groups for ${base}:`, e);
      return [];
    }
  }

  private async themeIds(read: () => Promise<any[]>): Promise<string[]> {
    try {
      return (await read() || []).map((t: any) => t?.id).filter((id: unknown) => typeof id === 'string' && id);
    } catch {
      return [];
    }
  }
}
